import type {
  KYCQuestionDefinition,
  KYCQuestionnaireElement,
  KYCQuestionnaireElementLayout,
} from '@anchorage/graphql/generated';
import {
  KYCQuestionnaireElementLayoutOrientation,
  KYCQuestionnaireElementType,
} from '@anchorage/graphql/generated';
import type { CustomLinkProps } from '@anchorage/utils';
import { parseSanitize } from '@anchorage/utils';
import { Card } from '@latitude/card';
import { Text } from '@latitude/text';
import type { ComponentType } from 'react';
import { Fragment } from 'react';

import { useQuestionnaire } from '../../hooks/useQuestionnaire/useQuestionnaire';
import { getBlockSubtitleColor } from '../../hooks/useQuestionnaire/utils';
import { QuestionWrapper } from '../QuestionWrapper';

type Props = {
  elements: KYCQuestionnaireElement[];
  answeredOptions: Record<string, string>;
  isFormDisabled?: boolean;
  level?: number;
  allQuestions: KYCQuestionDefinition[];
  CustomLink?: ComponentType<CustomLinkProps>;
  isBackoffice?: boolean;
  trackFn?: (questionId: string) => void;
};

function Elements({
  elements,
  answeredOptions,
  isFormDisabled = false,
  level,
  allQuestions,
  CustomLink,
  isBackoffice = false,
  trackFn,
}: Props) {
  const { shouldShowElement } = useQuestionnaire();

  if (!elements.length) {
    return null;
  }

  return (
    <>
      {elements?.map((element) => {
        switch (element.type) {
          case KYCQuestionnaireElementType.QUESTION:
            if (!element.question) {
              return null;
            }
            return (
              <QuestionWrapper
                key={element.id}
                question={element.question || undefined}
                isFormDisabled={isFormDisabled}
                answeredOptions={answeredOptions}
                layout={
                  (element.layout as KYCQuestionnaireElementLayout) || undefined
                }
                CustomLink={CustomLink}
                allQuestions={allQuestions}
                isBackoffice={isBackoffice}
                trackFn={trackFn}
              />
            );
          case KYCQuestionnaireElementType.BLOCK:
            if (level === 0) {
              return (
                <Fragment key={element.id}>
                  <div className="flex flex-col gap-2">
                    <Text variant="headingLarge" data-testid="page-title">
                      {element.title}
                    </Text>
                    {element.subtitle && (
                      <Text variant="bodyRegular" data-testid="page-subtitle">
                        {parseSanitize(element.subtitle, CustomLink)}
                      </Text>
                    )}
                  </div>
                  {!!element.elements?.length && (
                    <Elements
                      elements={element.elements}
                      isFormDisabled={isFormDisabled}
                      answeredOptions={answeredOptions}
                      level={level + 1}
                      allQuestions={allQuestions}
                      CustomLink={CustomLink}
                      isBackoffice={isBackoffice}
                      trackFn={trackFn}
                    />
                  )}
                </Fragment>
              );
            }
            if (level === 1) {
              const shouldShow = shouldShowElement?.({
                triggerOptionID: element?.triggeredBy,
                answeredOptions,
              });
              return shouldShow ? (
                <Card key={element.id} className="mt-6 p-6">
                  {element.title && (
                    <Text variant="headingMedium" color="strong">
                      {element.title}
                    </Text>
                  )}
                  {element.subtitle && (
                    <Text
                      variant="bodyRegular"
                      color={getBlockSubtitleColor(element.layout)}
                      data-testid="section-subtitle"
                      className="mt-1 whitespace-pre-line [&_ul_li]:ml-4 [&_ul_li]:list-disc"
                    >
                      {parseSanitize(element.subtitle, CustomLink)}
                    </Text>
                  )}
                  {!!element.elements?.length && (
                    <Elements
                      elements={element.elements}
                      isFormDisabled={isFormDisabled}
                      answeredOptions={answeredOptions}
                      level={level + 1}
                      allQuestions={allQuestions}
                      CustomLink={CustomLink}
                      isBackoffice={isBackoffice}
                      trackFn={trackFn}
                    />
                  )}
                </Card>
              ) : null;
            }
            {
              const shouldShow = shouldShowElement?.({
                triggerOptionID: element?.triggeredBy,
                answeredOptions,
              });

              if (!shouldShow) {
                return null;
              }

              const children = element.elements?.length ? (
                <Elements
                  elements={element.elements}
                  isFormDisabled={isFormDisabled}
                  answeredOptions={answeredOptions}
                  level={(level ?? 2) + 1}
                  allQuestions={allQuestions}
                  CustomLink={CustomLink}
                  isBackoffice={isBackoffice}
                  trackFn={trackFn}
                />
              ) : null;

              return (
                <div key={element.id}>
                  {element.title && (
                    <Text variant="headingTiny" className="mt-4" color="strong">
                      {parseSanitize(element.title, CustomLink)}
                    </Text>
                  )}
                  {element.subtitle && (
                    <Text
                      variant="bodyRegular"
                      color={getBlockSubtitleColor(element.layout)}
                      data-testid="nested-section-subtitle"
                      className="mt-1 whitespace-pre-line [&_ul_li]:ml-4 [&_ul_li]:list-disc"
                    >
                      {parseSanitize(element.subtitle, CustomLink)}
                    </Text>
                  )}
                  {/* The row classes go on a wrapper holding only the children, so
                      a title is never a flex item. Vertical blocks keep the
                      children as direct siblings of the title — Question's
                      `:not(:first-child)` top margin depends on that. */}
                  {children &&
                  element.layout?.orientation ===
                    KYCQuestionnaireElementLayoutOrientation.HORIZONTAL ? (
                    <div
                      data-testid="nested-section-row"
                      className="flex w-full items-start gap-6 [&>div:first-child]:mt-4 [&>div]:flex-1 [&>div]:basis-0 [&>div]:self-start [&_input]:h-[26px]"
                    >
                      {children}
                    </div>
                  ) : (
                    children
                  )}
                </div>
              );
            }
          default:
            return null;
        }
      })}
    </>
  );
}

export { Elements };
